import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  castVote,
  createPoll,
  deletePoll,
  getPoll,
  listOpenPollsAwaitingVote,
  listPolls,
  type CreatePollInput,
  type ListPollsFilters,
} from '@/lib/polls'

export function usePolls(filters: ListPollsFilters = {}, enabled = true) {
  return useQuery({
    queryKey: ['polls', filters],
    queryFn: () => listPolls(filters),
    enabled,
  })
}

export function useOpenPollsAwaitingVote() {
  return useQuery({
    queryKey: ['polls', 'awaiting-vote'],
    queryFn: () => listOpenPollsAwaitingVote(),
  })
}

export function usePoll(id: string | undefined) {
  return useQuery({
    queryKey: ['polls', id],
    queryFn: () => getPoll(id!),
    enabled: !!id,
  })
}

export function useCreatePoll() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreatePollInput) => createPoll(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['polls'] }),
  })
}

export function useCastVote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ pollId, optionId }: { pollId: string; optionId: string }) =>
      castVote(pollId, optionId),
    onSuccess: (_data, { pollId }) => {
      qc.invalidateQueries({ queryKey: ['polls', pollId] })
      qc.invalidateQueries({ queryKey: ['polls'] })
    },
  })
}

export function useDeletePoll() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deletePoll(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['polls'] }),
  })
}
